import Query from '@/utils/query-builder'
import { ARTICLE_LOAD_NUMBER } from '@/utils/press-options'

export const articleCategories = (locale) => {
  return new Query(
    'allPressCategories',
    [
      'id',
      'name',
      'slug',
      { name: 'alternatives: _allSlugLocales', fields: ['locale', 'value'] }
    ],
    {
      locale
    },
    'articleCategories'
  )
}

export const materialCategories = (locale) => {
  return new Query(
    'allPressMaterialCategories',
    [
      'id',
      'name',
      'slug',
      { name: 'alternatives: _allSlugLocales', fields: ['locale', 'value'] }
    ],
    {
      locale
    },
    'materialCategories'
  )
}

export const materials = (locale, categoryId = 'start', skip = 0) => {
  const fields = [
    'type: __typename',
    'id',
    'title',
    { name: 'file', fields: ['url'] },
    { name: 'thumbnail', fields: ['url'] },
    { name: 'categories', fields: ['name', { name: 'alternatives: _allSlugLocales', fields: ['locale', 'value'] }] }
  ]
  const args = {
    locale,
    orderBy: 'createdAt_DESC',
    first: ARTICLE_LOAD_NUMBER,
    skip,
    filter: {
      file: {
        exists: true
      }
    }
  }

  if (categoryId && categoryId !== 'start') {
    args.filter.categories = {
      anyIn: categoryId
    }
  }
  const queries = []

  const articlesQuery = new Query('allPressMaterials', fields, args, 'materials')
  queries.push(articlesQuery.build)

  if (skip === 0) {
    const metaArgs = { locale, filter: { file: { exists: true } } }
    if (categoryId && categoryId !== 'start') {
      metaArgs.filter.categories = {
        anyIn: categoryId
      }
    }
    const meta = new Query('_allPressMaterialsMeta', ['count'], metaArgs, 'meta')
    queries.push(meta.build)
  }
  return queries
}

export const articles = (locale, categoryId = 'start', skip = 0) => {
  const fields = [
    'type: __typename',
    'id',
    'title',
    'description',
    'slug',
    { name: 'publisher', fields: [{ name: 'logo', fields: ['url'] }, 'title'] },
    { name: 'categories', fields: ['name', { name: 'alternatives: _allSlugLocales', fields: ['locale', 'value'] }] },
    { name: 'featuredImage', fields: ['url'] },
    {
      name: 'content',
      fields: [
        'type: __typename',
        {
          name: '... on PressArticleFileRecord',
          fields: [
            { name: 'file', fields: ['url', 'filename'] }
          ]
        },
        {
          name: '... on PressArticleLinkRecord',
          fields: ['url']
        }
      ]
    },
    'createdAt'
  ]
  const args = {
    locale,
    first: ARTICLE_LOAD_NUMBER,
    skip,
    filter: {
      slug: {
        neq: null
      }
    }
  }

  if (categoryId && categoryId !== 'start') {
    args.filter.categories = {
      anyIn: categoryId
    }
  }
  const queries = []

  const articlesQuery = new Query('allPressArticles', fields, args, 'articles')
  queries.push(articlesQuery.build)

  if (skip === 0) {
    const metaArgs = { locale, filter: { slug: { neq: null } } }
    if (categoryId && categoryId !== 'start') {
      metaArgs.filter.categories = {
        anyIn: categoryId
      }
    }
    const meta = new Query('_allPressArticlesMeta', ['count'], metaArgs, 'meta')
    queries.push(meta.build)

    args.filter = { ...args.filter, ...{ featuredArticle: { eq: 'true' }, featuredImage: { exists: 'true' } } }
    const featured = new Query('allPressArticles', fields, args, 'featuredArticle')
    queries.push(featured.build)
  }

  return queries
}

export const articlesByTag = (locale, tagId, skip = 0) => {
  const fields = [
    'type: __typename',
    'id',
    'title',
    'description',
    'slug',
    { name: 'publisher', fields: [{ name: 'logo', fields: ['url'] }, 'title'] },
    { name: 'categories', fields: ['name', { name: 'alternatives: _allSlugLocales', fields: ['locale', 'value'] }] },
    { name: 'featuredImage', fields: ['url'] },
    {
      name: 'content',
      fields: [
        'type: __typename',
        {
          name: '... on PressArticleFileRecord',
          fields: [
            { name: 'file', fields: ['url', 'filename'] }
          ]
        },
        {
          name: '... on PressArticleLinkRecord',
          fields: ['url']
        }
      ]
    },
    'createdAt'
  ]
  const args = {
    locale,
    orderBy: 'createdAt_DESC',
    first: ARTICLE_LOAD_NUMBER,
    skip,
    filter: {
      slug: {
        neq: null
      },
      tags: {
        anyIn: tagId
      }
    }
  }

  const queries = []

  const articlesQuery = new Query('allPressArticles', fields, args, 'articles')
  queries.push(articlesQuery.build)

  if (skip === 0) {
    const metaArgs = { locale, filter: { slug: { neq: null }, tags: { eq: tagId } } }

    const meta = new Query('_allPressArticlesMeta', ['count'], metaArgs, 'meta')
    queries.push(meta.build)
  }
  return queries
}

export const article = (locale, slug) => {
  return new Query(
    'pressArticle',
    [
      'type: __typename',
      'id',
      'title',
      'slug',
      'description',
      { name: 'publisher', fields: [{ name: 'logo', fields: ['url'] }, 'title'] },
      { name: 'featuredImage', fields: ['url', 'alt'] },
      { name: 'categories', fields: ['id', 'name'] },
      {
        name: 'content',
        fields: [
          {
            name: '... on PressDefaultArticleRecord',
            fields: [
              'type: __typename',
              'body',
              {
                name: 'relatedArticles',
                fields: [
                  'type: __typename',
                  'title',
                  'description',
                  'slug',
                  { name: 'publisher', fields: [{ name: 'logo', fields: ['url'] }, 'title'] },
                  { name: 'featuredImage', fields: ['url', 'alt'] },
                  { name: 'categories', fields: ['id', 'name'] },
                  {
                    name: 'content',
                    fields: [
                      'type: __typename',
                      {
                        name: '... on PressArticleFileRecord',
                        fields: [
                          { name: 'file', fields: ['url', 'filename'] }
                        ]
                      },
                      {
                        name: '... on PressArticleLinkRecord',
                        fields: ['url']
                      }
                    ]
                  }
                ]
              },
              {
                name: 'relatedMaterials',
                fields: ['type: __typename', 'id', 'title', { name: 'file', fields: ['url'] },
                  { name: 'thumbnail', fields: ['url'] }]
              }
            ]
          },
          {
            name: '... on PressArticleLinkRecord',
            fields: [
              'type: __typename',
              'url'
            ]
          },
          {
            name: '... on PressArticleFileRecord',
            fields: [
              'type: __typename',
              { name: 'file', fields: ['url', 'filename'] }
            ]
          }
        ]
      },
      { name: 'tags', fields: ['name', 'slug'] },
      { name: 'alternatives: _allSlugLocales', fields: ['locale', 'value'] }
    ],
    {
      locale,
      filter: {
        slug: {
          eq: slug
        }
      }
    }
  )
}

export const tag = (locale, slug) => {
  return new Query(
    'allPressTags',
    [
      'id',
      'name',
      'slug',
      { name: 'alternatives: _allSlugLocales', fields: ['locale', 'value'] }
    ],
    { locale, filter: { slug: { eq: slug } } },
    'tags'
  )
}
